import moment from 'moment';
import { REPORT_FILTER_QUERY } from '@/constants/urlQuery';
import { mergeSearchParams, parseSearchParams } from '@/utils/urlSearch';

/**
 * Read shared report filter fields from a query string. Used by every
 * report-style page (dashboards, timeline, app usage, screenshots, url
 * tracking, teams reporting, user attendance) so links between them can
 * preserve member, date range, and timezone.
 * Invalid date ranges are dropped (member/tz are left as-is for validation elsewhere).
 *
 * @param {string} search - `location.search`
 * @returns {{ member: string | null, start: string | null, end: string | null, tz: string | null, team: string | null, status: string | null }}
 */
export function getReportFiltersFromSearch(search) {
  const q = parseSearchParams(search);
  const str = (key) =>
    typeof q[key] === 'string' ? q[key].trim() || null : null;

  let start = str(REPORT_FILTER_QUERY.start);
  let end = str(REPORT_FILTER_QUERY.end);
  const member = str(REPORT_FILTER_QUERY.member);
  const tz = str(REPORT_FILTER_QUERY.tz);
  const team = str(REPORT_FILTER_QUERY.team);
  const status = str(REPORT_FILTER_QUERY.status);

  if (start && !moment(start, 'YYYY-MM-DD', true).isValid()) start = null;
  if (end && !moment(end, 'YYYY-MM-DD', true).isValid()) end = null;
  if (start && end && moment(start).isAfter(end)) {
    start = null;
    end = null;
  }

  return { member, start, end, tz, team, status };
}

/**
 * Payload for mergeSearchParams to set report filters.
 * Pass empty string to remove a key.
 */
export function buildReportFilterUpdates({
  memberId,
  teamId,
  start,
  end,
  tz,
  status,
}) {
  return {
    [REPORT_FILTER_QUERY.member]: memberId ?? '',
    [REPORT_FILTER_QUERY.team]: teamId ?? '',
    [REPORT_FILTER_QUERY.start]: start ?? '',
    [REPORT_FILTER_QUERY.end]: end ?? '',
    [REPORT_FILTER_QUERY.tz]: tz ?? '',
    [REPORT_FILTER_QUERY.status]: status ?? '',
  };
}

/**
 * Merges report filter keys into an existing query (keeps cid, flow params, etc.).
 *
 * @param {string} currentSearch - `location.search`
 * @param {{ memberId?: string, teamId?: string, start?: string, end?: string, tz?: string, status?: string }} filters
 * @returns {string} - search string with leading `?` or empty
 */
export function mergeReportFiltersIntoSearch(currentSearch, filters) {
  return mergeSearchParams(
    currentSearch,
    buildReportFilterUpdates({
      memberId: filters.memberId ?? '',
      teamId: filters.teamId ?? '',
      start: filters.start ?? '',
      end: filters.end ?? '',
      tz: filters.tz ?? '',
      status: filters.status ?? '',
    })
  );
}

/**
 * Default date range for report pages: URL start/end if valid, else today.
 *
 * @param {string} [search] - `location.search` or full URL search part
 */
export function getInitialReportDateRangeFromSearch(search = '') {
  const { start, end } = getReportFiltersFromSearch(search);
  const today = moment().format('YYYY-MM-DD');
  return {
    start: start || today,
    end: end || today,
  };
}
